import { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../lib/auth.jsx";
import api, { formatApiError } from "../../lib/api";
import NotifyMeBell from "../../components/NotifyMeBell";
import FollowButton from "../../components/FollowButton";
import ContactTruckButton from "../../components/ContactTruckButton";
import DirectionsButton from "../../components/DirectionsButton";
import FoundingProBadge from "../../components/dashboard/FoundingProBadge";
import { fmtET, etTimeMs } from "../../lib/etTime";
import { PUBLIC_WEB_ORIGIN } from "../../lib/backendBase";
import {
  MapPin, NavigationArrow, Share, Bell, Storefront, Clock, CalendarBlank,
  Globe, FacebookLogo, InstagramLogo, TiktokLogo, EnvelopeSimple, Phone,
  ShoppingBag, Star, Warning, ArrowLeft, ArrowRight, ListBullets, X,
  CaretLeft, CaretRight, Lock, ArrowSquareOut,
} from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Sticky-header back link on the public profile page. Replaces the
 * previous hardcoded `<Link to="/map">` so that a customer landing on
 * the profile from the LIST view (`/map?view=list`) returns to the
 * list — not the map. We rely on `navigate(-1)` whenever the user
 * has prior history, falling back to `/map` for direct-link visits
 * (e.g., from a shared social link or search engine).
 */
function ProfileBackToTrucks() {
  const navigate = useNavigate();
  const handleClick = () => {
    // If the user arrived via a real in-app nav (list view, map, etc.)
    // there will be history to pop. window.history.state is null only
    // on a cold load.
    if (typeof window !== "undefined" && window.history.length > 1) {
      navigate(-1);
    } else {
      navigate("/map");
    }
  };
  return (
    <button
      type="button"
      onClick={handleClick}
      className="text-xs font-semibold text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
      data-testid="public-back-to-map"
    >
      <ArrowLeft weight="bold" className="h-3.5 w-3.5" /> Find more trucks
    </button>
  );
}

/**
 * TruckProfilePreview — admin-only preview of the v1 truck profile page.
 *
 * Hidden from production by:
 *   - Route guard (only mounted when `user.is_admin === true`)
 *   - Backend route gated by `require_admin`
 *   - No public navigation links to it
 */
export default function TruckProfilePreview() {
  const { truckId } = useParams();
  const { user, loading: authLoading } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAllEvents, setShowAllEvents] = useState(false);

  useEffect(() => {
    if (!truckId || !user?.is_admin) return;
    let mounted = true;
    setLoading(true);
    api
      .get(`/admin/truck-profile/${encodeURIComponent(truckId)}`)
      .then(({ data }) => mounted && setData(data))
      .catch(() => mounted && toast.error("Couldn't load profile preview"))
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, [truckId, user]);

  if (authLoading) return null;
  if (!user?.is_admin) {
    return (
      <div className="max-w-md mx-auto p-8 text-center">
        <Warning weight="duotone" className="h-10 w-10 text-rose-500 mx-auto mb-3" />
        <h1 className="font-display font-bold text-xl mb-2">Admins only</h1>
        <p className="text-sm text-muted-foreground">This profile preview is gated to admins until launch.</p>
        <Link to="/" className="mt-4 inline-block text-sm text-primary hover:underline">← Back to map</Link>
      </div>
    );
  }
  if (loading) return <ProfileSkeleton />;
  if (!data) return <div className="p-8 text-center text-muted-foreground">No profile found.</div>;

  return <ProfileBody data={data} showAllEvents={showAllEvents} onToggleEvents={() => setShowAllEvents((v) => !v)} />;
}

/* -- Composed body — split out so the editor can re-use it for "Step 5: Preview" */
export function ProfileBody({ data, showAllEvents, onToggleEvents, embedded = false, isPublic = false }) {
  const draft = data.draft || {};
  const status = data.status || { kind: "no_events", label: "No upcoming stops" };
  const events = data.events || [];
  const insights = data.insights || [];
  const ordering = draft.ordering || {};
  const focalY = clamp(Number(draft.cover_focal_y ?? 50), 0, 100);

  // Pro gating — when the API tells us the owner is non-Pro we render
  // a stripped page (truck name + events + status + map only) plus a
  // single upgrade CTA. The admin preview path always renders the
  // full editor view regardless of Pro state (admins are previewing
  // their own work).
  //
  // Feb 2026 update — visual identity is NO LONGER Pro-gated. Free
  // trucks that upload logo + cover + description now show them on
  // the public profile just like Pro trucks (matches the un-gating
  // in `routers/public_profiles.py`). Only the Pro-tier extras —
  // menu file, gallery, ordering, contact info, social links, hours,
  // granular insights — stay behind `showProSections`.
  const ownerIsPro = data.owner_is_pro !== false; // default true (admin/legacy)
  const showProSections = ownerIsPro;
  // Visual identity — logo, cover, description — renders for any
  // published truck. The backend authoritatively decides whether the
  // URL is present in `data.logo_url` / `data.cover_url`, so a truthy
  // check here is sufficient.
  const showVisualIdentity = true;

  // Validate ordering URL — strict http/https only, exclude social fallbacks.
  const orderingUrl = typeof ordering.external_url === "string" ? ordering.external_url.trim() : "";
  const orderingValid = showProSections
    && !!ordering.enabled
    && ordering.type === "external"
    && /^https?:\/\//i.test(orderingUrl)
    && !isSocialUrl(orderingUrl);

  const truckOpen = status.kind === "open";
  const liveOrNextEventId = useMemo(() => liveOrNextId(events), [events]);

  const visibleEvents = showAllEvents ? events : events.slice(0, 3);
  const fallbackCover = "linear-gradient(135deg, rgb(234 88 12 / 0.85), rgb(180 83 9 / 0.95))";

  // Address frequency for "Regular stop" tag
  const addrCounts = useMemo(() => {
    const m = {};
    events.forEach((e) => {
      const a = (e.address || "").trim();
      if (a) m[a] = (m[a] || 0) + 1;
    });
    return m;
  }, [events]);

  // Lightbox for the photo gallery
  const galleryUrls = data.gallery_urls || [];
  const [lightboxIdx, setLightboxIdx] = useState(-1);

  return (
    <div className="min-h-dvh bg-background pb-20">
      {!embedded && !isPublic && (
        <div className="sticky top-0 z-30 bg-background/90 backdrop-blur-md border-b border-border">
          <div className="max-w-2xl mx-auto px-3 py-2.5 flex items-center gap-2">
            <Link
              to="/admin"
              className="h-9 w-9 grid place-items-center rounded-full hover:bg-muted text-muted-foreground"
              data-testid="profile-back"
            >
              <ArrowLeft weight="bold" className="h-4 w-4" />
            </Link>
            <div className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">
              Admin preview · v1
            </div>
            <Link
              to="/admin/profile-editor"
              className="ml-auto text-xs font-semibold text-primary hover:underline inline-flex items-center gap-1"
            >
              <ListBullets weight="bold" className="h-3.5 w-3.5" /> All drafts
            </Link>
          </div>
        </div>
      )}
      {!embedded && isPublic && (
        <div className="sticky top-0 z-30 bg-background/90 backdrop-blur-md border-b border-border">
          <div className="max-w-2xl mx-auto px-3 py-2.5 flex items-center gap-2">
            <ProfileBackToTrucks />
          </div>
        </div>
      )}

      {/* HERO — cover, then logo + name BELOW (no overlap) */}
      <div className="relative" data-testid="profile-hero">
        <div
          className="h-56 sm:h-80 lg:h-96 w-full bg-cover"
          style={
            showVisualIdentity && data.cover_url
              ? { backgroundImage: `url(${data.cover_url})`, backgroundPosition: `center ${focalY}%` }
              : { background: fallbackCover, backgroundPosition: "center" }
          }
        />

        <div className="max-w-2xl mx-auto px-4 mt-4 sm:mt-5">
          {/* Logo + name + tags in one row — avatar pattern, fully below cover */}
          <div className="flex items-center gap-3 sm:gap-4">
            <div
              className="h-20 w-20 sm:h-24 sm:w-24 rounded-2xl border border-border bg-white shadow-sm grid place-items-center overflow-hidden flex-shrink-0 p-1.5"
              data-testid="profile-logo"
            >
              {showVisualIdentity && data.logo_url ? (
                <img
                  src={data.logo_url}
                  alt={`${draft.truck_name || "Truck"} logo`}
                  className="h-full w-full object-contain"
                  loading="eager"
                  decoding="async"
                />
              ) : (
                <Storefront weight="duotone" className="h-10 w-10 text-primary" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h1
                className="font-display font-bold text-2xl sm:text-3xl tracking-tight break-words leading-tight"
                data-testid="profile-name"
              >
                {draft.truck_name || "Untitled Truck"}
              </h1>
              {data.owner_is_founding_pro && (
                <div className="mt-1.5" data-testid="profile-founding-pro">
                  <FoundingProBadge size="sm" />
                </div>
              )}
              {draft.cuisine_tags?.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid="profile-tags">
                  {draft.cuisine_tags.slice(0, 6).map((t) => (
                    <span key={t} className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-primary/10 text-primary">
                      {t}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          {showProSections && (draft.short_description || draft.known_for) && (
            <div className="mt-3 space-y-1.5">
              {draft.short_description && (
                <p className="text-sm text-foreground" data-testid="profile-description">{draft.short_description}</p>
              )}
              {draft.known_for && (
                <p className="text-sm text-muted-foreground" data-testid="profile-known-for">
                  <Star weight="duotone" className="inline h-3.5 w-3.5 text-primary mr-1 -mt-0.5" />
                  Known for: {draft.known_for}
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* LIVE STATUS — countdown-aware */}
      <div className="max-w-2xl mx-auto px-4 mt-5">
        <StatusBanner status={status} />
      </div>

      {/* PRIMARY ACTIONS — Directions filled, Order conditional, others ghost.
          Pro adds a "Contact" button in the row so visitors can send the
          owner an inquiry without ever seeing their email. */}
      <div className="max-w-2xl mx-auto px-4 mt-4">
        <div
          className={
            "grid gap-2 " +
            (orderingValid
              ? (ownerIsPro ? "grid-cols-3 sm:grid-cols-6" : "grid-cols-3 sm:grid-cols-5")
              : (ownerIsPro ? "grid-cols-3 sm:grid-cols-5" : "grid-cols-2 sm:grid-cols-4"))
          }
          data-testid="profile-actions"
        >
          <DirectionsButton
            lat={status.next_event?.lat}
            lng={status.next_event?.lng}
            address={status.next_event?.address}
            label={draft.truck_name}
            disabled={!status.next_event?.lat}
            variant="filled"
            testId="action-directions"
          />
          {orderingValid && (
            <ActionButton
              icon={ShoppingBag}
              label={ordering.button_label || "Order"}
              tone={truckOpen ? "filled" : "ghost"}
              disabled={!truckOpen}
              title={truckOpen ? "Order online" : "Order opens when the truck is live"}
              onClick={() => {
                if (!truckOpen) return;
                window.open(orderingUrl, "_blank", "noopener,noreferrer");
              }}
              testId="action-order"
            />
          )}
          <NotifyMeBell
            truckId={data.truck_id}
            truckName={draft.truck_name}
            variant="action"
            data-testid="action-notify"
          />
          <FollowButton
            truckId={data.truck_id}
            truckName={draft.truck_name}
            compact
            data-testid="action-follow"
          />
          {ownerIsPro && data.truck_slug && (
            <ContactTruckButton
              slug={data.truck_slug}
              truckName={draft.truck_name}
            />
          )}
          <ActionButton
            icon={Share}
            label="Share"
            tone="ghost"
            onClick={async () => {
              const publicUrl = data.truck_slug
                ? `${PUBLIC_WEB_ORIGIN}/t/${data.truck_slug}`
                : `${PUBLIC_WEB_ORIGIN}/api/truck/${data.truck_id}/share`;
              if (navigator.share) {
                try { await navigator.share({ url: publicUrl }); } catch { /* user cancelled */ }
              } else {
                try {
                  await navigator.clipboard?.writeText(publicUrl);
                  toast.success("Link copied");
                } catch {
                  toast.error("Could not copy link");
                }
              }
            }}
            testId="action-share"
          />
        </div>
        {orderingValid && !truckOpen && (
          <p className="mt-2 text-[11px] text-muted-foreground text-center" data-testid="order-disabled-note">
            Online ordering opens when the truck is live.
          </p>
        )}
        {orderingValid && truckOpen && ordering.notes && (
          <p className="mt-2 text-[11px] text-muted-foreground text-center">{ordering.notes}</p>
        )}
      </div>

      {/* UPCOMING EVENTS */}
      <Section title="Upcoming">
        {events.length === 0 ? (
          <EmptyState text="No upcoming stops posted yet. Follow this truck to know when they're back." />
        ) : (
          <div className="space-y-2.5" data-testid="profile-events">
            {visibleEvents.map((ev) => {
              const isLiveOrNext = (ev.id || `${ev.starts_at}-${ev.address}`) === liveOrNextEventId;
              const override = (draft.event_overrides || {})[ev.id];
              return (
                <EventCard
                  key={ev.id || `${ev.starts_at}-${ev.address}`}
                  event={ev}
                  override={override}
                  truckOrdering={orderingValid ? ordering : null}
                  showOrdering={isLiveOrNext}
                  isRegularStop={addrCounts[(ev.address || "").trim()] >= 3}
                  truckName={draft.truck_name}
                />
              );
            })}
            {events.length > 3 && (
              <button
                type="button"
                onClick={onToggleEvents}
                className="w-full h-10 rounded-xl border border-border text-sm font-semibold hover:bg-muted transition"
                data-testid="profile-events-toggle"
              >
                {showAllEvents ? "Show less" : `Show ${events.length - 3} more`}
              </button>
            )}
          </div>
        )}
      </Section>

      {/* PHOTOS — Pro only */}
      {showProSections && galleryUrls.length > 0 && (
        <Section title="Photos">
          <div
            className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4 snap-x snap-mandatory"
            data-testid="profile-gallery"
          >
            {galleryUrls.map((u, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setLightboxIdx(i)}
                className="h-32 w-44 rounded-xl overflow-hidden snap-start flex-shrink-0 bg-muted relative group"
                data-testid={`profile-gallery-thumb-${i}`}
                aria-label={`Open photo ${i + 1}`}
              >
                <img
                  src={u}
                  alt=""
                  className="h-full w-full object-cover transition-transform group-hover:scale-105"
                  loading="lazy"
                  decoding="async"
                />
              </button>
            ))}
          </div>
        </Section>
      )}

      {showProSections && lightboxIdx >= 0 && (
        <Lightbox
          urls={galleryUrls}
          index={lightboxIdx}
          onClose={() => setLightboxIdx(-1)}
          onPrev={() => setLightboxIdx((i) => (i - 1 + galleryUrls.length) % galleryUrls.length)}
          onNext={() => setLightboxIdx((i) => (i + 1) % galleryUrls.length)}
        />
      )}

      {/* MENU / LINKS — Pro only */}
      {showProSections && (
        <Section title="Links & menu">
          <div className="space-y-2" data-testid="profile-links">
            {draft.menu_url && <LinkRow icon={ListBullets} label="View menu" href={ensureHttp(draft.menu_url)} />}
            {data.menu_file_url && <LinkRow icon={ListBullets} label="Menu (PDF/image)" href={data.menu_file_url} />}
            {draft.website && <LinkRow icon={Globe} label={prettyUrl(draft.website)} href={ensureHttp(draft.website)} />}
            {draft.facebook && <LinkRow icon={FacebookLogo} label="Facebook" href={ensureHttp(draft.facebook)} />}
            {draft.instagram && <LinkRow icon={InstagramLogo} label="Instagram" href={ensureHttp(draft.instagram)} />}
            {draft.tiktok && <LinkRow icon={TiktokLogo} label="TikTok" href={ensureHttp(draft.tiktok)} />}
            {draft.contact_email && <LinkRow icon={EnvelopeSimple} label={draft.contact_email} href={`mailto:${draft.contact_email}`} />}
            {draft.contact_phone && <LinkRow icon={Phone} label={draft.contact_phone} href={`tel:${draft.contact_phone}`} />}
            {!draft.menu_url && !data.menu_file_url && !draft.website && !draft.facebook && !draft.instagram && !draft.tiktok && !draft.contact_email && !draft.contact_phone && (
              <EmptyState text="No links added yet." />
            )}
          </div>
        </Section>
      )}

      {/* INSIGHTS — Pro only, only when real data exists */}
      {showProSections && insights.length > 0 && (
        <Section title="TruckTrackr Insights">
          <ul className="space-y-1.5" data-testid="profile-insights">
            {insights.map((ins, i) => (
              <li key={i} className="flex items-center gap-2 text-sm">
                <Clock weight="duotone" className="h-4 w-4 text-primary" />
                {ins.label}
              </li>
            ))}
          </ul>
        </Section>
      )}

      {/* LOCKED TEASERS + UPGRADE CTA — non-Pro public profiles only */}
      {!showProSections && isPublic && (
        <>
          <LockedSection title="Description" testId="locked-description">
            A short blurb (Pro). E.g. "Texas-style brisket smoked low and slow…"
          </LockedSection>
          <LockedSection title="Photos" testId="locked-gallery">
            <LockedGalleryStrip />
          </LockedSection>
          <LockedSection title="Menu" testId="locked-menu">
            A linked menu PDF / image so customers know what to order before they arrive.
          </LockedSection>
          <LockedSection title="Links" testId="locked-links">
            Website, Facebook, Instagram, TikTok, phone, email — all clickable.
          </LockedSection>
          <LockedSection title="Branding" testId="locked-branding">
            Cover photo + logo + custom hero so the page actually looks like your truck.
          </LockedSection>

          <Section title="">
            <UpgradeCta
              truckSlug={data.truck_slug || ""}
              authoritative
            />
          </Section>
        </>
      )}
    </div>
  );
}

/* ---- Pro-gating UI helpers (locked teasers + claim/upgrade CTA) ---- */

function LockedSection({ title, testId, children }) {
  return (
    <Section title={
      <span className="inline-flex items-center gap-1.5">
        {title}
        <span className="inline-flex items-center gap-1 h-5 px-2 rounded-full bg-amber-500/15 text-amber-700 dark:text-amber-300 text-[10px] font-bold uppercase tracking-wider">
          <Lock weight="fill" className="h-2.5 w-2.5" /> Pro
        </span>
      </span>
    }>
      <div
        className="relative rounded-2xl border border-dashed border-border bg-muted/30 p-5 overflow-hidden"
        data-testid={testId}
      >
        <div className="text-sm text-muted-foreground blur-[1.5px] select-none pointer-events-none">
          {children}
        </div>
        <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-t from-card/70 to-transparent pointer-events-none">
          <div className="inline-flex items-center gap-1.5 h-7 px-3 rounded-full bg-card/95 border border-border text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
            <Lock weight="duotone" className="h-3 w-3" /> Unlock with Pro
          </div>
        </div>
      </div>
    </Section>
  );
}

function LockedGalleryStrip() {
  return (
    <div className="flex gap-2 overflow-hidden" aria-hidden>
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="h-24 w-32 rounded-xl bg-gradient-to-br from-muted to-muted-foreground/20 flex-shrink-0"
        />
      ))}
    </div>
  );
}

function UpgradeCta({ truckSlug, authoritative }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  const handleClick = useCallback(async () => {
    // Anonymous → bounce to signup, preserve return-to claim slug so
    // the claim API call resumes after auth.
    if (!user) {
      const next = `/t/${encodeURIComponent(truckSlug)}?claim=1`;
      navigate(`/signup?role=owner&claim=${encodeURIComponent(truckSlug)}&next=${encodeURIComponent(next)}`);
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/profile/${encodeURIComponent(truckSlug)}/claim`, {
        note: null,
      });
      // Backend tells us where to go next.
      if (data?.action === "upgrade") {
        navigate(data.redirect || `/upgrade?claim=${truckSlug}`);
      } else if (data?.action === "request_submitted") {
        toast.success(data.message || "Ownership request sent — we'll review it shortly.");
        navigate(data.redirect || "/dashboard");
      } else {
        navigate("/dashboard");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't submit claim");
    } finally {
      setBusy(false);
    }
  }, [user, truckSlug, navigate]);

  // Auto-trigger when the page mounts with ?claim=1 (set by the signup
  // redirect above) — completes the flow without requiring another click.
  useEffect(() => {
    if (!authoritative) return;
    if (typeof window === "undefined") return;
    const sp = new URLSearchParams(window.location.search);
    if (!sp.get("claim")) return;
    if (!user) return; // wait for auth to load
    // Strip the query param so reloads don't keep claiming.
    const url = new URL(window.location.href);
    url.searchParams.delete("claim");
    window.history.replaceState({}, "", url.toString());
    handleClick();
  }, [user, authoritative, handleClick]);

  return (
    <div
      className="rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/10 to-amber-500/10 p-5 sm:p-6"
      data-testid="profile-upgrade-cta"
    >
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-xl bg-primary/15 grid place-items-center flex-shrink-0">
          <Star weight="duotone" className="h-5 w-5 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-wider font-bold text-primary mb-1">
            TruckTrackr Pro
          </p>
          <h3 className="font-display font-bold text-lg sm:text-xl leading-tight mb-1.5">
            Claim and upgrade this profile with TruckTrackr Pro.
          </h3>
          <p className="text-sm text-muted-foreground mb-4">
            Add a cover photo, gallery, description, menu, hours, social links, and online
            ordering. Stand out in the directory — and turn searches into directions.
          </p>
          <button
            type="button"
            onClick={handleClick}
            disabled={busy}
            className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full bg-primary text-primary-foreground font-semibold text-sm hover:bg-primary/90 transition-colors disabled:opacity-60"
            data-testid="profile-upgrade-cta-button"
          >
            {busy ? "Working…" : <>Claim &amp; upgrade <ArrowRight weight="bold" className="h-4 w-4" /></>}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---- helpers + small components ---- */

function clamp(n, lo, hi) { return Math.max(lo, Math.min(hi, isNaN(n) ? lo : n)); }

function isSocialUrl(url) {
  try {
    const host = new URL(url).host.toLowerCase().replace(/^www\./, "");
    return /(^|\.)(facebook|fb|instagram|tiktok|x|twitter|threads|youtube|youtu)\.(com|be)$/i.test(host);
  } catch {
    return false;
  }
}

function liveOrNextId(events) {
  const now = Date.now();
  const parse = (s) => { try { return new Date(s).getTime(); } catch { return NaN; } };
  // Live first
  for (const ev of events) {
    const s = parse(ev.starts_at), e = parse(ev.ends_at);
    if (!isNaN(s) && !isNaN(e) && s <= now && now <= e) return ev.id || `${ev.starts_at}-${ev.address}`;
  }
  // Else first upcoming
  for (const ev of events) {
    const s = parse(ev.starts_at);
    if (!isNaN(s) && s > now) return ev.id || `${ev.starts_at}-${ev.address}`;
  }
  return null;
}

function StatusBanner({ status }) {
  const tone = status.kind === "open" ? "emerald" : status.kind === "opens_later" ? "amber" : "muted";
  const palette = {
    emerald: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30",
    amber: "bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30",
    muted: "bg-muted text-muted-foreground border-border",
  };
  // Build a countdown line from next_event when present
  const ev = status.next_event;
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(t);
  }, []);
  let countdown = "";
  if (ev) {
    const sMs = ev.starts_at ? new Date(ev.starts_at).getTime() : NaN;
    const eMs = ev.ends_at ? new Date(ev.ends_at).getTime() : NaN;
    if (!isNaN(sMs) && !isNaN(eMs) && sMs <= now && now <= eMs) {
      countdown = `closes in ${humanDuration(eMs - now)}`;
    } else if (!isNaN(sMs) && sMs > now) {
      countdown = `opens in ${humanDuration(sMs - now)}`;
    }
  }
  return (
    <div
      className={`rounded-2xl border px-4 py-3 flex items-center gap-2.5 ${palette[tone]}`}
      data-testid="profile-status"
    >
      <span className={`h-2 w-2 rounded-full ${status.kind === "open" ? "bg-emerald-500 animate-pulse" : "bg-current opacity-50"}`} />
      <div className="flex flex-col leading-tight">
        <span className="text-sm font-semibold">{status.label}</span>
        {countdown && (
          <span className="text-[11px] opacity-80 mt-0.5" data-testid="profile-status-countdown">{countdown}</span>
        )}
      </div>
    </div>
  );
}

function humanDuration(ms) {
  const m = Math.max(0, Math.round(ms / 60000));
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60), mins = m % 60;
  if (h < 24) return mins ? `${h}h ${mins}m` : `${h}h`;
  const d = Math.floor(h / 24), hr = h % 24;
  return hr ? `${d}d ${hr}h` : `${d}d`;
}

function ActionButton({ icon: Icon, label, onClick, disabled, tone = "ghost", testId, title }) {
  const filled = tone === "filled";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      data-testid={testId}
      className={
        "h-12 rounded-2xl flex flex-col items-center justify-center gap-0.5 text-[11px] font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed " +
        (filled
          ? "bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm"
          : "bg-card border border-border hover:bg-muted text-foreground")
      }
    >
      <Icon weight={filled ? "fill" : "duotone"} className="h-4 w-4" />
      {label}
    </button>
  );
}

function Section({ title, children }) {
  return (
    <section className="max-w-2xl mx-auto px-4 mt-6">
      <h2 className="font-display font-bold text-base mb-2 tracking-tight">{title}</h2>
      {children}
    </section>
  );
}

function EmptyState({ text }) {
  return <div className="rounded-xl border border-dashed border-border bg-muted/20 p-4 text-sm text-muted-foreground text-center">{text}</div>;
}

function LinkRow({ icon: Icon, label, href }) {
  const external = href.startsWith("http");
  // Programmatic open: works around iOS Chrome/Safari ignoring
  // target="_blank" on cross-origin PDF/image attachments. Falling
  // back to the anchor's native behavior when window.open is blocked.
  const handleClick = (e) => {
    if (!external) return; // mailto: / tel: — let the anchor handle it
    e.preventDefault();
    try {
      const w = window.open(href, "_blank", "noopener,noreferrer");
      if (!w) {
        // Popup-blocked — fall back to same-window nav so the click
        // never becomes a dead-end.
        window.location.assign(href);
      }
    } catch {
      window.location.assign(href);
    }
  };
  return (
    <a
      href={href}
      target={external ? "_blank" : undefined}
      rel={external ? "noopener noreferrer" : undefined}
      onClick={handleClick}
      aria-label={external ? `${label} (opens in new tab)` : label}
      className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl bg-card border border-border hover:bg-muted active:bg-muted/70 transition text-sm"
    >
      <Icon weight="duotone" className="h-4 w-4 text-primary flex-shrink-0" />
      <span className="truncate flex-1">{label}</span>
      {external && (
        <ArrowSquareOut
          weight="regular"
          className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0"
          aria-hidden
        />
      )}
    </a>
  );
}

function EventCard({ event, override, truckOrdering, showOrdering, isRegularStop, truckName }) {
  // NOTE: `truckName` is passed as a prop because we used to reference
  // `draft.truck_name` here — but `draft` lives in ProfileBody's scope,
  // not EventCard's, which made every /t/:slug page with at least one
  // event blow up with `draft is not defined` and render as a black
  // screen (real prod bug report May 2026, Suzy Q's Coffee).
  const dt = useMemo(() => {
    const weekday = fmtET(event.starts_at, { weekday: "short" });
    if (!weekday) return { weekday: "?", date: "?", time: "?" };
    return {
      weekday,
      date: fmtET(event.starts_at, { month: "short", day: "numeric" }),
      time: fmtET(event.starts_at, { hour: "numeric", minute: "2-digit" }),
    };
  }, [event.starts_at]);
  const endTime = useMemo(() => {
    return fmtET(event.ends_at, { hour: "numeric", minute: "2-digit" }) || null;
  }, [event.ends_at]);

  // Live = now is within [starts_at, ends_at]
  const isLive = useMemo(() => {
    try {
      const now = Date.now();
      const s = etTimeMs(event.starts_at);
      const e = etTimeMs(event.ends_at);
      return !isNaN(s) && !isNaN(e) && s <= now && now <= e;
    } catch { return false; }
  }, [event.starts_at, event.ends_at]);

  // Per-event override wins, else truck-level (if provided as `truckOrdering`).
  const overrideValid = override?.ordering_enabled
    && /^https?:\/\//i.test(override.ordering_url || "")
    && !isSocialUrl(override.ordering_url || "");
  const orderingHere = showOrdering
    ? overrideValid
      ? { url: override.ordering_url, notes: override.notes, label: "Order Online" }
      : truckOrdering
      ? { url: truckOrdering.external_url, notes: truckOrdering.notes, label: truckOrdering.button_label }
      : null
    : null;

  return (
    <div className="rounded-2xl border border-border bg-card p-3.5">
      <div className="flex items-start gap-3">
        <div className="text-center flex-shrink-0 w-12 rounded-lg bg-primary/5 py-1.5 border border-primary/20">
          <div className="text-[10px] uppercase font-bold tracking-wider text-primary">{dt.weekday}</div>
          <div className="font-display font-bold text-base leading-tight text-foreground">{dt.date}</div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-semibold text-sm truncate">{event.event_name || event.address || "Stop"}</div>
            {isRegularStop && (
              <span className="text-[9px] uppercase tracking-wider font-bold text-emerald-700 dark:text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-full px-1.5 py-0.5">
                Regular stop
              </span>
            )}
          </div>
          <div className="text-xs text-muted-foreground mt-0.5 inline-flex items-center gap-1">
            <Clock weight="duotone" className="h-3 w-3" />
            {dt.time}{endTime && ` – ${endTime}`}
          </div>
          {event.address && (
            <div className="text-xs text-foreground/80 mt-1 flex items-start gap-1">
              <MapPin weight="duotone" className="h-3.5 w-3.5 mt-0.5 flex-shrink-0 text-primary" />
              <span className="truncate">{event.address}</span>
            </div>
          )}
        </div>
      </div>
      {(event.lat && event.lng) || orderingHere ? (
        <div className="flex gap-2 mt-3">
          {event.lat && event.lng && (
            <div className="flex-1 inline-flex">
              <DirectionsButton
                lat={event.lat}
                lng={event.lng}
                address={event.address}
                label={truckName}
                variant="ghost"
                testId={`event-directions-${event.event_id || ''}`}
              />
            </div>
          )}
          {orderingHere && (
            isLive ? (
              <a
                href={ensureHttp(orderingHere.url)}
                target="_blank" rel="noopener noreferrer"
                className="flex-1 h-9 rounded-xl text-xs font-bold bg-primary text-primary-foreground inline-flex items-center justify-center gap-1.5"
                data-testid="event-order"
              >
                <ShoppingBag weight="duotone" className="h-3.5 w-3.5" />
                {orderingHere.label || "Order"}
              </a>
            ) : (
              <button
                type="button"
                disabled
                title="Online ordering opens when this event goes live"
                className="flex-1 h-9 rounded-xl text-xs font-semibold bg-muted text-muted-foreground inline-flex items-center justify-center gap-1.5 opacity-60 cursor-not-allowed"
                data-testid="event-order-disabled"
              >
                <ShoppingBag weight="duotone" className="h-3.5 w-3.5" />
                {orderingHere.label || "Order"}
              </button>
            )
          )}
        </div>
      ) : null}
    </div>
  );
}

function Lightbox({ urls, index, onClose, onPrev, onNext }) {
  const handleKey = useCallback((e) => {
    if (e.key === "Escape") onClose();
    else if (e.key === "ArrowLeft") onPrev();
    else if (e.key === "ArrowRight") onNext();
  }, [onClose, onPrev, onNext]);
  useEffect(() => {
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleKey);
    };
  }, [handleKey]);
  const url = urls[index];
  return (
    <div
      className="fixed inset-0 z-[1100] bg-black/95 grid place-items-center p-4 animate-in fade-in"
      onClick={onClose}
      data-testid="profile-lightbox"
    >
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        className="absolute top-4 right-4 h-10 w-10 grid place-items-center rounded-full bg-white/10 hover:bg-white/20 text-white"
        aria-label="Close"
        data-testid="lightbox-close"
      >
        <X weight="bold" className="h-5 w-5" />
      </button>
      {urls.length > 1 && (
        <>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onPrev(); }}
            className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 h-11 w-11 grid place-items-center rounded-full bg-white/10 hover:bg-white/20 text-white"
            aria-label="Previous"
            data-testid="lightbox-prev"
          >
            <CaretLeft weight="bold" className="h-6 w-6" />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onNext(); }}
            className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 h-11 w-11 grid place-items-center rounded-full bg-white/10 hover:bg-white/20 text-white"
            aria-label="Next"
            data-testid="lightbox-next"
          >
            <CaretRight weight="bold" className="h-6 w-6" />
          </button>
        </>
      )}
      <img
        src={url}
        alt=""
        onClick={(e) => e.stopPropagation()}
        className="max-h-[88vh] max-w-[92vw] object-contain rounded-lg shadow-2xl touch-manipulation"
        style={{ touchAction: "pinch-zoom" }}
      />
      {urls.length > 1 && (
        <div className="absolute bottom-5 left-0 right-0 flex justify-center gap-1.5">
          {urls.map((_, i) => (
            <span
              key={i}
              className={"h-1.5 rounded-full transition-all " + (i === index ? "w-6 bg-white" : "w-1.5 bg-white/40")}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ProfileSkeleton() {
  return (
    <div className="min-h-dvh bg-background">
      <div className="h-44 sm:h-56 lg:h-64 w-full bg-muted animate-pulse" />
      <div className="max-w-2xl mx-auto px-4 -mt-10 sm:-mt-12 relative">
        <div className="flex items-end gap-3 sm:gap-4">
          <div className="h-20 w-20 sm:h-24 sm:w-24 rounded-2xl border-4 border-card bg-muted animate-pulse flex-shrink-0" />
          <div className="flex-1 pb-1 space-y-2">
            <div className="h-8 w-2/3 bg-muted rounded animate-pulse" />
            <div className="h-4 w-1/2 bg-muted rounded animate-pulse" />
          </div>
        </div>
      </div>
    </div>
  );
}

function ensureHttp(url) {
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  return `https://${url}`;
}
function prettyUrl(url) {
  try { return new URL(ensureHttp(url)).host; } catch { return url; }
}
